import { CloseCircleOutlined, DeleteOutlined, DownOutlined, EditOutlined, InfoCircleOutlined, MoreOutlined, UpOutlined } from "@ant-design/icons";
import { Avatar, Button, Col, Dropdown, Input, List, Mentions, Modal, Row, Select, Space, Tag, Tooltip, Typography } from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const sustainedTooltipText = "Select Yes, if you believe that this annotation will persist for an extended period of time and may not be a single task. ";

const AnnotationItem = ({ annotation, users = [], fields = [], onUpdate, onDelete, permission, historyDisplayMode }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({
        content: annotation.content,
        type: annotation.type,
        state: annotation.state,
        priority: annotation.priority,
        assignedTo: annotation.assignedTo,
        targetName: annotation.targetName,
        sustained: annotation.sustained,
    });
    const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
    const [closeComment, setCloseComment] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const closeCommentText = annotation.closeComment;
    const closedByName = annotation.updatedByName;
    const closedDateLabel = annotation.updatedDate ? moment(annotation.updatedDate).fromNow() : null;
    const systemComments = annotation.systemGeneratedComments || [];
    const hasHistory = annotation.version > 1 && systemComments.length > 0;
    const useHistoryModal = historyDisplayMode === "modal";

    useEffect(() => {
        setShowHistory(false);
        setHistoryModalVisible(false);
    }, [annotation.annotationId, useHistoryModal]);

    const allKnownUsersMap = useMemo(() => {
        return users
            .filter((user) => !user.deleted)
            .reduce((acc, user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                acc[fullName] = user;
                return acc;
            }, {});
    }, [users]);

    const assignedUser = useMemo(() => {
        return users.find((user) => user.userId === annotation.assignedTo);
    }, [users, annotation.assignedTo]);

    const mentionableUsersMap = useMemo(() => {
        return users
            .filter((user) => user.active && !user.deleted)
            .reduce((acc, user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                acc[fullName] = user.userId;
                return acc;
            }, {});
    }, [users]);

    const mentionOptions = useMemo(() => {
        return users
            .filter((user) => !user.deleted)
            .map((user) => {
                const fullName = `${user.firstName} ${user.lastName}`;
                return {
                    key: user.userId,
                    value: fullName,
                    disabled: !user.active,
                    label: (
                        <>
                            <span style={{ textDecoration: !user.active ? "line-through" : "none" }}>{fullName}</span>
                            {!user.active && <span style={{ color: "#8c8c8c", marginLeft: 8 }}>(Inactive)</span>}
                        </>
                    ),
                };
            });
    }, [users]);

    if (!permission?.canView) {
        return null;
    }

    const handleUpdate = () => {
        if (editedData.content.trim()) {
            const updatePayload = {
                content: editedData.content.trim(),
                ...(editedData.type && { type: editedData.type }),
                ...(editedData.state && { state: editedData.state }),
                ...(editedData.priority && { priority: editedData.priority }),
                ...(editedData.assignedTo && { assignedTo: editedData.assignedTo }),
                ...(editedData.targetName && { targetName: editedData.targetName }),
                ...(editedData.sustained !== undefined && { sustained: editedData.sustained }),
            };
            onUpdate(annotation.annotationId, updatePayload);
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedData({
            content: annotation.content,
            type: annotation.type,
            state: annotation.state,
            priority: annotation.priority,
            assignedTo: annotation.assignedTo,
            targetName: annotation.targetName,
            sustained: annotation.sustained,
        });
    };

    const renderContentWithMentions = (content) => {
        if (!content || !users.length) return content;

        const userNames = Object.keys(allKnownUsersMap);
        if (userNames.length === 0) return content;

        const escapedUserNames = userNames.map((name) => name.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"));
        const regex = new RegExp(`@(${escapedUserNames.join("|")})\\b`, "g");

        const elements = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
            if (match.index > lastIndex) {
                elements.push(content.substring(lastIndex, match.index));
            }

            const fullName = match[1];
            const user = allKnownUsersMap[fullName];

            elements.push(
                <span
                    key={match.index}
                    style={{
                        color: "#1890ff",
                        fontWeight: "bold",
                        textDecoration: !user.active ? "line-through" : "none",
                        opacity: !user.active ? 0.75 : 1,
                    }}
                >
                    @{fullName}
                </span>,
            );

            lastIndex = regex.lastIndex;
        }

        if (lastIndex < content.length) {
            elements.push(content.substring(lastIndex));
        }

        return elements;
    };

    const getTypeColor = (type) => {
        switch (type) {
            case "issue":
                return "red";
            case "review":
                return "orange";
            case "info":
                return "blue";
            default:
                return "default";
        }
    };

    const getStateColor = (state) => {
        switch (state) {
            case "open":
                return "blue";
            case "in_progress":
                return "orange";
            case "resolved":
                return "green";
            case "dismissed":
                return "gray";
            default:
                return "default";
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high":
                return "red";
            case "medium":
                return "orange";
            case "low":
                return "green";
            default:
                return "default";
        }
    };

    const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const extractTaggedUserIds = (text) => {
        if (!text) {
            return [];
        }
        const taggedIds = new Set();
        Object.entries(mentionableUsersMap).forEach(([fullName, userId]) => {
            const mentionRegex = new RegExp(`@${escapeRegex(fullName)}\\b`, "g");
            if (mentionRegex.test(text)) {
                taggedIds.add(userId);
            }
        });
        return Array.from(taggedIds);
    };

    const getCloseModalPopupContainer = () => {
        if (typeof document === "undefined") {
            return undefined;
        }
        const modalSelector = `.close-annotation-modal-${annotation.annotationId}`;
        const modalContent = document.querySelector(`${modalSelector} .ant-modal-content`);
        const modalWrapper = document.querySelector(modalSelector);
        return modalContent || modalWrapper || document.body;
    };

    const formatHistoryValue = (value) => {
        if (value === null || value === undefined || value === "") {
            return "(empty)";
        }
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }
        return String(value);
    };

    const formatFieldLabel = (field) => {
        if (!field) return "";
        const spaced = field
            .replace(/_/g, " ")
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/\s+/g, " ")
            .trim();
        return spaced
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const renderHistoryEntries = () =>
        systemComments.map((comment, index) => (
            <div key={comment.id || comment.createdDate} style={{ borderLeft: "2px solid #9fb6ff", paddingLeft: 12, marginBottom: index === systemComments.length - 1 ? 12 : 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <Text strong>{comment.triggeredBy?.name || "System"}</Text>
                    {comment.createdDate && (
                        <Tooltip title={moment(comment.createdDate).format("llll")}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {moment(comment.createdDate).fromNow()}
                            </Text>
                        </Tooltip>
                    )}
                </div>
                {comment.message && (
                    <div style={{ marginTop: 6 }}>
                        <Text type="secondary">
                            <Text strong style={{ fontSize: 14 }}>
                                System Generated Comment :
                            </Text>{" "}
                            {comment.message}
                        </Text>
                    </div>
                )}
                {comment.changes?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {comment.changes.map((change, changeIndex) => (
                            <div
                                key={`${comment.id || comment.createdDate}-${change.field}-${changeIndex}`}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    padding: "6px 0",
                                    borderTop: changeIndex === 0 ? "none" : "1px dashed #e0e0e0",
                                }}
                            >
                                <Text style={{ flex: "0 0 160px", fontWeight: 500 }}>{formatFieldLabel(change.field)}</Text>
                                <div style={{ flex: "1 1 auto" }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {formatHistoryValue(change.from)}
                                    </Text>
                                    <Text style={{ margin: "0 6px" }}>→</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {formatHistoryValue(change.to)}
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {index !== systemComments.length - 1 && <div style={{ marginTop: 12, borderBottom: "1px solid #e5e5e5" }}></div>}
            </div>
        ));

    const historyButtonLabel = useHistoryModal ? "Show history" : showHistory ? "Hide history" : "Show history";

    const handleHistoryButtonClick = () => {
        if (useHistoryModal) {
            setHistoryModalVisible(true);
        } else {
            setShowHistory((prev) => !prev);
        }
    };

    const openCloseModal = () => {
        setCloseComment("");
        setIsCloseModalVisible(true);
    };

    const handleCloseAnnotation = () => {
        const trimmed = closeComment.trim();
        if (!trimmed) {
            return;
        }
        const taggedUsers = extractTaggedUserIds(trimmed);
        onUpdate(annotation.annotationId, {
            closed: true,
            close_comment: trimmed,
            close_comment_tagged_users: taggedUsers,
        });
        setIsCloseModalVisible(false);
        setCloseComment("");
    };

    const handleMenuClick = ({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key === "edit") {
            setIsEditing(true);
        } else if (key === "delete") {
            onDelete(annotation.annotationId);
        } else if (key === "close") {
            openCloseModal();
        }
    };

    const menuItems = (() => {
        const items = [];
        if (permission?.canEdit) {
            items.push({
                key: "edit",
                icon: <EditOutlined />,
                label: "Edit",
            });
        }
        if (permission?.canEdit && !annotation.closed) {
            items.push({
                key: "close",
                icon: <CloseCircleOutlined />,
                label: "Close",
            });
        }
        if (permission?.canDelete) {
            items.push({
                key: "delete",
                icon: <DeleteOutlined />,
                label: "Delete",
                danger: true,
            });
        }
        return items;
    })();

    const hasAnyAction = menuItems.length > 0;

    const actions = hasAnyAction
        ? [
              <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={["click"]} key="actions">
                  <button
                      type="button"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}
                      aria-label="More options"
                      onClick={(e) => e.stopPropagation()}
                  >
                      <MoreOutlined />
                  </button>
              </Dropdown>,
          ]
        : [];

    const title = (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <Text strong>{annotation.createdByName}</Text>
                <Tooltip title={moment(annotation.createdDate).format("llll")}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {moment(annotation.createdDate).fromNow()}
                    </Text>
                </Tooltip>
            </div>
            <Space align="center" size={[4, 4]} wrap>
                {annotation.type && <Tag color={getTypeColor(annotation.type)}>{annotation.type.toUpperCase()}</Tag>}
                {annotation.state && <Tag color={getStateColor(annotation.state)}>{annotation.state.replace("_", " ").toUpperCase()}</Tag>}
                {annotation.priority && <Tag color={getPriorityColor(annotation.priority)}>{annotation.priority.toUpperCase()}</Tag>}
                {assignedUser && (
                    <Tag>
                        Assigned to: {assignedUser.firstName} {assignedUser.lastName}
                    </Tag>
                )}
                {annotation.sustained && <Tag color="purple">SUSTAINED</Tag>}
            </Space>
        </div>
    );

    const description = (
        <>
            {annotation.targetName && (
                <div style={{ marginBottom: "8px" }}>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        Target: <Text strong>{annotation.targetName}</Text>
                    </Text>
                </div>
            )}

            {!isEditing && annotation.closed && closeCommentText && (
                <div
                    style={{
                        background: "#f1f5ff",
                        borderLeft: "4px solid #1890ff",
                        borderRadius: 8,
                        padding: "8px 10px",
                        marginBottom: 12,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Closed by
                        </Text>
                        {closedByName && <Text style={{ fontSize: 12 }}>{closedByName}</Text>}
                        {closedDateLabel && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                • {closedDateLabel}
                            </Text>
                        )}
                    </div>
                    <Text style={{ display: "block", marginTop: 6, color: "#1c1c1c", fontStyle: "italic", whiteSpace: "pre-wrap" }}>{closeCommentText}</Text>
                </div>
            )}

            {isEditing ? (
                <>
                    <TextArea
                        rows={3}
                        value={editedData.content}
                        onChange={(event) => setEditedData({ ...editedData, content: event.target.value })}
                        style={{ marginTop: 4, marginBottom: 12 }}
                        placeholder="Update annotation content"
                        allowClear
                    />

                    <Row gutter={12} style={{ marginBottom: 12 }}>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2 }}>Type</div>
                            <Select
                                placeholder="Type"
                                value={editedData.type}
                                onChange={(value) => setEditedData({ ...editedData, type: value })}
                                allowClear
                                size="small"
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="issue">Issue</Select.Option>
                                <Select.Option value="review">Review</Select.Option>
                                <Select.Option value="info">Info</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2 }}>State</div>
                            <Select
                                placeholder="State"
                                value={editedData.state}
                                onChange={(value) => setEditedData({ ...editedData, state: value })}
                                allowClear
                                size="small"
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="open">Open</Select.Option>
                                <Select.Option value="in_progress">In Progress</Select.Option>
                                <Select.Option value="resolved">Resolved</Select.Option>
                                <Select.Option value="dismissed">Dismissed</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                    <Row gutter={12} style={{ marginBottom: 12 }}>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2 }}>Priority</div>
                            <Select
                                placeholder="Priority"
                                value={editedData.priority}
                                onChange={(value) => setEditedData({ ...editedData, priority: value })}
                                allowClear
                                size="small"
                                style={{ width: "100%" }}
                            >
                                <Select.Option value="low">Low</Select.Option>
                                <Select.Option value="medium">Medium</Select.Option>
                                <Select.Option value="high">High</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                                Sustained
                                <Tooltip title={sustainedTooltipText} placement="top">
                                    <InfoCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
                                </Tooltip>
                            </div>
                            <Select
                                placeholder="Sustained"
                                value={editedData.sustained}
                                onChange={(value) => setEditedData({ ...editedData, sustained: value })}
                                allowClear
                                size="small"
                                style={{ width: "100%" }}
                            >
                                <Select.Option value={true}>Yes</Select.Option>
                                <Select.Option value={false}>No</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                    <Row gutter={12} style={{ marginBottom: 12 }}>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2 }}>Assign To</div>
                            <Select
                                placeholder="User"
                                value={editedData.assignedTo}
                                onChange={(value) => setEditedData({ ...editedData, assignedTo: value })}
                                showSearch
                                allowClear
                                size="small"
                                style={{ width: "100%" }}
                            >
                                {users
                                    .filter((user) => user.active && !user.deleted)
                                    .map((user) => (
                                        <Select.Option key={user.userId} value={user.userId}>
                                            {`${user.firstName} ${user.lastName}`}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ fontSize: 12, marginBottom: 2 }}>Target Field</div>
                            <Select
                                placeholder="Field"
                                value={editedData.targetName}
                                onChange={(value) => setEditedData({ ...editedData, targetName: value })}
                                allowClear
                                showSearch
                                size="small"
                                style={{ width: "100%" }}
                            >
                                {fields
                                    .filter((f) => f && f.name)
                                    .map((f) => (
                                        <Select.Option key={f.fieldId || f.name} value={f.name}>
                                            {f.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Col>
                    </Row>

                    <Row justify="end" align="middle">
                        <Space>
                            <Button
                                size="small"
                                type="primary"
                                onClick={handleUpdate}
                                disabled={
                                    (editedData.content === annotation.content &&
                                        editedData.type === annotation.type &&
                                        editedData.state === annotation.state &&
                                        editedData.priority === annotation.priority &&
                                        editedData.assignedTo === annotation.assignedTo &&
                                        editedData.targetName === annotation.targetName &&
                                        editedData.sustained === annotation.sustained) ||
                                    editedData.content.trim() === ""
                                }
                            >
                                Save
                            </Button>
                            <Button size="small" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Row>
                </>
            ) : (
                <Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>{renderContentWithMentions(annotation.content)}</Paragraph>
            )}

            {hasHistory && !isEditing && (
                <div style={{ marginTop: 12 }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={handleHistoryButtonClick}
                        icon={!useHistoryModal && showHistory ? <UpOutlined /> : <DownOutlined />}
                        style={{ paddingLeft: 0 }}
                    >
                        {historyButtonLabel}
                    </Button>
                    {!useHistoryModal && showHistory && (
                        <div style={{ marginTop: 8, padding: "12px 16px", border: "1px solid #e6ecf5", borderRadius: 8, background: "#fafbff" }}>{renderHistoryEntries()}</div>
                    )}
                </div>
            )}
        </>
    );

    return (
        <>
            <List.Item
                id={`annotation-${annotation.annotationId}`}
                actions={hasAnyAction ? actions : undefined}
                style={{ background: "#fff", padding: "12px 0", alignItems: "flex-start", border: "none", borderBottom: "1px solid #f0f0f0" }}
            >
                <List.Item.Meta avatar={<Avatar>{annotation.createdByName.charAt(0)}</Avatar>} title={title} description={description} />
            </List.Item>
            <Modal
                title="Close annotation"
                open={isCloseModalVisible}
                okText="Close annotation"
                onOk={handleCloseAnnotation}
                onCancel={() => {
                    setIsCloseModalVisible(false);
                    setCloseComment("");
                }}
                wrapClassName={`close-annotation-modal close-annotation-modal-${annotation.annotationId}`}
                okButtonProps={{ disabled: closeComment.trim() === "" }}
            >
                <p>Please provide a reason or summary for closing this annotation.</p>
                <Mentions
                    className="comment-mentions-input"
                    rows={4}
                    value={closeComment}
                    onChange={(value) => setCloseComment(value)}
                    placeholder="Add a close comment"
                    notFoundContent="No users found"
                    getPopupContainer={getCloseModalPopupContainer}
                    style={{ width: "100%" }}
                    options={mentionOptions}
                />
            </Modal>
            {useHistoryModal && hasHistory && (
                <Modal
                    title="Annotation history"
                    open={historyModalVisible}
                    onCancel={() => setHistoryModalVisible(false)}
                    footer={null}
                    width={640}
                    styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
                >
                    <div style={{ paddingRight: 8 }}>{renderHistoryEntries()}</div>
                </Modal>
            )}
        </>
    );
};

export default AnnotationItem;
